import * as React from 'react';
import { observer } from 'mobx-react';
import { PageConfirmMobileNumberViewModel } from './page-confirm-mobile-number-view-model';
import { PayerOnboardingUserAction } from '../../payer-onboarding-user-actions';
import { SubmitButton } from '../../../WebGiving/components/submit-button/submit-button';
import { Form } from '../../../WebGiving/primitives/form';
import { LeftChevronLink, Link, DisabledLink } from '../../../WebGiving/primitives/link/link';
import { TextboxField } from '../../../WebGiving/components/form-fields';
import { SvgIcon } from '../../../WebGiving/components/svg-icon';
import { FormControlWithIcon, FormControlIcon } from '../../../WebGiving/components/control-with-icon/control-with-icon';
import { ErrorMessage } from '../../components/error-message/error-message';
import { FadeTransition } from '../../../WebGiving/components/fade-transition';
import { PanelContentTransition, panelContentTransitionDuration } from '../../../WebGiving/components/panel-content-transition';
import { FormControlLabel } from '../../../WebGiving/primitives/label';
import { FieldValidationMessage } from '../../../WebGiving/components/form-control/form-control-validation-error';
import { Tooltip, TooltipPlacement } from '../../../WebGiving/components/tooltip/tooltip';
import { FocusOnMount } from '../../../WebGiving/components/focus-on-mount-hoc';
import { getPageClasses } from '../../../WebGiving/pages/helpers/get-page-classes';
import { PanelHeading } from '../../../WebGiving/components/panel-heading';
import { PanelFooter } from '../../../WebGiving/components/panel-footer';
import { ModalType } from '../../modals/modal-type';
import { ResetPageReason } from '../../payer-onboarding-generated';
import padlocksvg from '../../../WebGiving/assets/svg/padlock.svg';
import * as styles from './page-confirm-mobile-number.less';
import { standardButton } from '../../../WebGiving/components/tooltip/tooltip.less';
import { pageFooter } from '../../../WebGiving/main/webgiving-main.less';

export { PageConfirmMobileNumberViewModel };

@observer
export class PageConfirmMobileNumber extends React.Component<{
	vm: PageConfirmMobileNumberViewModel,
	raiseAction: (action: PayerOnboardingUserAction) => void,
	transitionState: string,
}> {
	panelBodyNode: HTMLDivElement;

	render() {
		const { vm, transitionState } = this.props;
		const {
			mobileNumber,
			error,
			securityCode,
			isLoading,
			resendCodeDisabled,
			pageShouldBeVisible,
			isBlocked,
		} = vm;

		const elementsShouldEnter = transitionState === 'entered';
		const securityCodeId = 'security-code';

		const resendCodeLink = resendCodeDisabled.get()
			? <Tooltip placement={TooltipPlacement.Bottom} buttonClassName={standardButton} content={`We can only send you a code every 20 seconds.`} hideOnMobile>
				<DisabledLink>Resend code</DisabledLink>
			</Tooltip>
			: <Link className={styles.resendCode} acceptanceTestTargetId="Resend code" onClick={this.handleResendCode} branded={false}>Resend code</Link>;

		return (
			<div className={getPageClasses(pageShouldBeVisible, isLoading)} data-pp-at-target={pageShouldBeVisible ? `Confirm Mobile Number page` : null}>
				<PanelHeading vm={vm}>
					<FadeTransition in={elementsShouldEnter}>
						<div>
							<h1 className="panel-title">Verify your mobile number</h1>
							<p className="panel-subtitle">Enter the security code sent to your number <strong className={styles.mobileNumber}>{mobileNumber}</strong>. {resendCodeLink}</p>

							<LeftChevronLink acceptanceTestTargetId="Change your number" onClick={this.handleChangeMobileNumber} branded={false}>
								Change your number
							</LeftChevronLink>
						</div>
					</FadeTransition>
				</PanelHeading>
				<Form onSubmit={this.handleSubmit}>
					<div className="panel-body panel-body-form" ref={this.savePanelBodyNode}>
						<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
							<div>
								<ErrorMessage
									error={error}
									onResetPage={this.handleResetPage}
								/>
								<div className="row">
									<div className={`col-sm-8 col-centered ${styles.securityCodeField}`}>
										<div className="form-control-container">
											<FormControlLabel formControlId={securityCodeId}>Security code</FormControlLabel>
											<FormControlWithIcon iconPlacement="right">
												<FocusOnMount delay={panelContentTransitionDuration}>
													<TextboxField
														formField={securityCode}
														formControlId={securityCodeId}
														acceptanceTestTargetId="Confirmation Code"
														numericKeyboard
														maxLength={6}
														additionalClassNames={securityCode.validationError ? 'form-control-invalid' : undefined}
													/>
												</FocusOnMount>
												<FormControlIcon><SvgIcon svg={padlocksvg} /></FormControlIcon>
											</FormControlWithIcon>
											{securityCode.validationError && <FieldValidationMessage errorMessage={securityCode.validationError} />}
										</div>
										<div className={styles.needHelp}><Link acceptanceTestTargetId="Need help" onClick={this.showHelp} branded={false}>Need help?</Link></div>
									</div>
								</div>
							</div>
						</PanelContentTransition>
					</div>
					<PanelFooter className={pageFooter}>
						<SubmitButton acceptanceTestTargetId="Next" isLoading={isLoading} isBlocked={isBlocked}>Confirm</SubmitButton>
					</PanelFooter>
				</Form>
			</div>
		);
	}

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private showHelp = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		this.props.raiseAction(new PayerOnboardingUserAction.OpenModalDialog(ModalType.NeedHelp));
	}

	private handleResendCode = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		this.props.raiseAction(new PayerOnboardingUserAction.RequestResendOfConfirmationCode());
	}

	private handleChangeMobileNumber = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		this.props.raiseAction(new PayerOnboardingUserAction.ChangeMobileNumber());
	}

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		this.props.raiseAction(new PayerOnboardingUserAction.SubmitCurrentPage());
	}

	private handleResetPage = (reason?: ResetPageReason) => {
		this.props.raiseAction(new PayerOnboardingUserAction.ResetPageBecauseOfError(reason));
	}
}
